/**
 * DIEUKYDIEU.TV - HTTP API CLIENT MODULE
 * Unified fetch wrapper with timeout, json formatting, error handling and status checks.
 * Under 300 lines rule compliant.
 */

import { API_CONFIG } from './config.js';

export class ApiError extends Error {
    constructor(message, status = 500, data = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

/**
 * Build URL with query params
 * @param {string} endpoint 
 * @param {Record<string, any>} [params] 
 * @returns {string}
 */
function buildUrl(endpoint, params) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const base = API_CONFIG.BASE_URL.replace(/\/+$/, '');
    const url = new URL(`${base}${cleanEndpoint}`);

    if (params && typeof params === 'object') {
        Object.keys(params).forEach(key => {
            const val = params[key];
            if (val !== undefined && val !== null && val !== '') {
                url.searchParams.append(key, String(val));
            }
        });
    }

    return url.toString();
}

/**
 * Execute HTTP request with timeout
 * @param {string} endpoint 
 * @param {RequestInit} [options] 
 * @param {Record<string, any>} [params] 
 */
async function request(endpoint, options = {}, params = null) {
    const url = buildUrl(endpoint, params);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT_MS || 8000);

    const headers = {
        ...API_CONFIG.DEFAULT_HEADERS,
        ...(options.headers || {})
    };

    try {
        const response = await fetch(url, {
            ...options,
            headers,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        let data = null;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            data = await response.text();
        }

        if (!response.ok) {
            const errorMsg = (data && data.message) || `HTTP error ${response.status}: ${response.statusText}`;
            throw new ApiError(errorMsg, response.status, data);
        }

        return data;
    } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
            throw new ApiError(`Request timeout after ${API_CONFIG.TIMEOUT_MS}ms`, 408);
        }
        if (err instanceof ApiError) {
            throw err;
        }
        throw new ApiError(err.message || 'Network request failed', 0, err);
    }
}

export const apiClient = {
    /**
     * HTTP GET request
     * @param {string} endpoint 
     * @param {Record<string, any>} [params] 
     * @param {RequestInit} [options] 
     */
    get(endpoint, params = null, options = {}) {
        return request(endpoint, { ...options, method: 'GET' }, params);
    },

    /**
     * HTTP POST request
     * @param {string} endpoint 
     * @param {any} body 
     * @param {RequestInit} [options] 
     */
    post(endpoint, body = null, options = {}) {
        return request(endpoint, {
            ...options,
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined
        });
    },

    /**
     * HTTP PUT request
     * @param {string} endpoint 
     * @param {any} body 
     * @param {RequestInit} [options] 
     */
    put(endpoint, body = null, options = {}) {
        return request(endpoint, {
            ...options,
            method: 'PUT',
            body: body ? JSON.stringify(body) : undefined
        });
    },

    /**
     * HTTP DELETE request
     * @param {string} endpoint 
     * @param {RequestInit} [options] 
     */
    delete(endpoint, options = {}) {
        return request(endpoint, { ...options, method: 'DELETE' });
    }
};
